import { api } from "./client";

// Deal Flow → Prequalification → Document Language Editor.
// Mirrors the C# records in Core/DTOs/Prequalification/PrequalDocumentEditorDtos
// (camelCase wire). All endpoints gate on Prequalification.ManageDocuments.

export const PREQUAL_DOCUMENT_KEYS = [
  "buyer-letter",
  "seller-letter",
  "buyer-deal-sheet",
  "seller-deal-sheet",
  "finalize-email",
] as const;
export type PrequalDocumentKey = (typeof PREQUAL_DOCUMENT_KEYS)[number];

export interface PrequalDocumentBlock {
  documentKey: string;
  blockKey: string;
  variant: string;
  label: string;
  defaultText: string;
  draftText?: string | null;
  publishedText?: string | null;
  effectiveText: string;
  locked: boolean;
  isCustomized: boolean;
  hasDraft: boolean;
  allowedTokens: string[];
  requiredTokens: string[];
  /** False when the block may be reworded but never left blank (the server rejects a
   *  blank publish). Absent on older payloads — treat as true. */
  allowEmpty?: boolean;
}

export interface PrequalDocumentVersionBlock {
  blockKey: string;
  variant: string;
  label: string;
  defaultText: string;
  publishedText: string;
}

export interface PrequalDocumentVersion {
  id: string;
  publishedAtUtc: string;
  publishedByUserId?: string | null;
  changedBlockCount: number;
  blocks: PrequalDocumentVersionBlock[];
}

export interface PrequalDocument {
  documentKey: string;
  label: string;
  audienceLabel: string;
  isEmail: boolean;
  blocks: PrequalDocumentBlock[];
  versions: PrequalDocumentVersion[];
}

/** One block-tagged validation failure — carried in a 400 response's `details`.
 *  `severity` distinguishes blocking Errors from publishable Warnings. */
export interface PrequalDocumentBlockError {
  blockKey: string;
  variant: string;
  message: string;
  severity: "Warning" | "Error";
}

export interface PrequalDocumentBlockRef {
  blockKey: string;
  variant: string;
}

/** A selectable document set: `{ id: null }` = Phoenix Default (today's copy);
 *  otherwise a partner brand's per-partner override set. Server-ordered, with
 *  Phoenix Default always first. */
export interface PrequalDocumentSet {
  id: string | null;
  label: string;
}

/** Result of a save-draft: the draft is always stored for an editable block;
 *  `errors` carries any Warning / Error findings so the editor surfaces them
 *  inline on save (warnings never block the draft). */
export interface PrequalDocumentDraftResult {
  ok: boolean;
  errors: PrequalDocumentBlockError[];
  block: PrequalDocumentBlock | null;
}

const BASE = "/deal-flow/prequalification/documents";

const seg = (v: string) => encodeURIComponent(v);

/** Append `?partnerBrandId=<guid>` (or `&…` when the path already has a query)
 *  ONLY for a partner set. For the Phoenix Default set (null/undefined) the URL
 *  is byte-identical to today's request. */
const withPartner = (path: string, partnerBrandId?: string | null) =>
  partnerBrandId ? `${path}${path.includes("?") ? "&" : "?"}partnerBrandId=${seg(partnerBrandId)}` : path;

export const prequalDocumentsApi = {
  /** The selectable document sets (Phoenix Default + each active partner brand). */
  sets: () => api.get<PrequalDocumentSet[]>(`${BASE}/sets`),

  list: (partnerBrandId?: string | null) =>
    api.get<PrequalDocument[]>(withPartner(BASE, partnerBrandId)),

  saveDraft: (
    documentKey: string,
    blockKey: string,
    variant: string,
    text: string,
    partnerBrandId?: string | null,
  ) =>
    api.put<PrequalDocumentDraftResult>(
      withPartner(`${BASE}/${seg(documentKey)}/blocks/${seg(blockKey)}`, partnerBrandId),
      { variant, text },
    ),

  publish: (
    documentKey: string,
    blocks?: PrequalDocumentBlockRef[],
    overrideWarnings?: boolean,
    partnerBrandId?: string | null,
  ) =>
    api.post<PrequalDocument>(withPartner(`${BASE}/${seg(documentKey)}/publish`, partnerBrandId), {
      blocks: blocks ?? null,
      overrideWarnings: overrideWarnings ?? false,
    }),

  restore: (documentKey: string, versionId: string, partnerBrandId?: string | null) =>
    api.post<PrequalDocument>(withPartner(`${BASE}/${seg(documentKey)}/restore`, partnerBrandId), {
      versionId,
    }),

  resetBlock: (
    documentKey: string,
    blockKey: string,
    variant: string,
    partnerBrandId?: string | null,
  ) =>
    api.delete<PrequalDocumentBlock>(
      withPartner(
        `${BASE}/${seg(documentKey)}/blocks/${seg(blockKey)}?variant=${seg(variant)}`,
        partnerBrandId,
      ),
    ),

  /** Render a preview with the current unsaved draft texts. Returns a Blob —
   *  a PDF (letters / deal sheets) or a text/html body (finalize-email) — that
   *  the editor loads into an iframe via an object URL. Persists nothing. */
  preview: (
    documentKey: string,
    blocks: { blockKey: string; variant: string; text: string }[],
    partnerBrandId?: string | null,
  ) => api.postBlob(withPartner(`${BASE}/${seg(documentKey)}/preview`, partnerBrandId), { blocks }),
};
